import React, { useRef, useEffect, useState, useCallback } from 'react';
import DataTable, { TableProps } from 'react-data-table-component';

interface ResizableTableProps extends TableProps<any> {
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  tableId: string;
}

const ResizableTable: React.FC<ResizableTableProps> = ({
  initialWidth = 800,
  minWidth = 300,
  maxWidth = 1200,
  tableId,
  ...tableProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Load saved width from localStorage
  useEffect(() => {
    const savedWidth = localStorage.getItem(`table-width-${tableId}`);
    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      if (parsedWidth >= minWidth && parsedWidth <= maxWidth) {
        setWidth(parsedWidth);
      }
    }
  }, [tableId, minWidth, maxWidth]);

  // Save width to localStorage
  const saveWidth = useCallback((newWidth: number) => {
    localStorage.setItem(`table-width-${tableId}`, newWidth.toString());
  }, [tableId]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
    
    // Add visual feedback
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startX;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
    setWidth(newWidth);
  }, [isResizing, startX, startWidth, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      saveWidth(width);
      
      // Remove visual feedback
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isResizing, width, saveWidth]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Handle double-click to reset to initial width
  const handleDoubleClick = useCallback(() => {
    setWidth(initialWidth);
    saveWidth(initialWidth);
  }, [initialWidth, saveWidth]);

  return (
    <div className="resizable-table-wrapper" style={{ marginBottom: '24px' }}>
      <div
        ref={containerRef}
        className="resizable-table-container"
        style={{
          width: `${width}px`,
          position: 'relative',
          transition: isResizing ? 'none' : 'border-color 0.2s ease',
        }}
      >
        <div
          className="resize-handle"
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          title="Drag to resize table width, double-click to reset"
        >
          <div className="resize-indicator" />
        </div>
        
        <div style={{ width: '100%', overflow: 'auto' }}>
          <DataTable
            {...tableProps}
            customStyles={{
              table: {
                style: {
                  minWidth: '100%',
                },
              },
              headRow: {
                style: {
                  backgroundColor: 'rgba(63, 124, 172, 0.3)',
                  minHeight: '48px',
                },
              },
              rows: {
                style: {
                  minHeight: '40px',
                  '&:hover': {
                    backgroundColor: 'rgba(36, 52, 77, 0.8)',
                    cursor: 'pointer',
                  },
                },
              },
            }}
          />
        </div>
        
        {/* Width indicator */}
        <div
          style={{
            position: 'absolute',
            top: '-25px',
            right: '0px',
            background: 'rgba(88, 147, 223, 0.9)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            opacity: isResizing ? 1 : 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          {width}px
        </div>
      </div>
    </div>
  );
};

export default ResizableTable;
